import React from "react";
import {
  Grid,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import { MoneyField } from "../../fields/MoneyField";
import { SectionAccordion } from "./SectionAccordion";
import TuneIcon from "@mui/icons-material/Tune";
import {
  OptionsSectionProps,
  ChangeCountOptionsSectionProps,
} from "../../../../types";

export interface OptionsSectionComponentProps {
  state: OptionsSectionProps;
  setState: (state: OptionsSectionProps) => void;
  formErrors: Record<string, string>;
  updateField: (field: string, value: string) => void;
  total: number;
  totalLaborMaterial: number;
  mode: "edit" | "review";
  pageVariant: "create" | "edit" | "view";
  initialState?: OptionsSectionProps;
  changeCount?: ChangeCountOptionsSectionProps;
  forceMobile?: boolean;
}

export function OptionsSection({
  state,
  setState,
  formErrors,
  updateField,
  total,
  totalLaborMaterial,
  mode,
  pageVariant,
  initialState,
  changeCount,
  forceMobile,
}: OptionsSectionComponentProps) {
  const isMobile = forceMobile === true;
  const isEditView = pageVariant !== "create";
  const isView = pageVariant === "view";
  const viewFieldSx = {
    "& .MuiOutlinedInput-root": { backgroundColor: "#fff" },
    "& .MuiOutlinedInput-input.Mui-disabled": { WebkitTextFillColor: "rgba(0,0,0,0.45)" },
    "& .MuiInputAdornment-root .MuiTypography-root": { color: "rgba(0,0,0,0.45)" },
  };
  const labelXs = isMobile ? 12 : (isEditView ? 6 : 9);
  const fieldXs = isMobile ? 12 : 3;

  const dollarAdornment = (
    <InputAdornment position="start" sx={{ ml: 1 }}>$</InputAdornment>
  );

  function initialText(val: number | undefined): string {
    return val === undefined ? "" : val.toString().replace(/^0+/, "");
  }

  function changeCountText(count: number): string {
    return count > 1 ? ` (${count})` : "";
  }

  return (
    <SectionAccordion
      id="options"
      title="Options"
      icon={
        <TuneIcon
          fontSize="large"
          sx={{ color: "#ACACAC" }}
        />
      }
      total={total}
      forceMobile={forceMobile}
    >
      {/* Cost of Options with Contract */}
      <Grid item xs={labelXs} display="flex">
        <Typography variant="body2" color="text.secondary" fontWeight={400} sx={{ alignSelf: "center" }}>
          Cost of Options with Contract
        </Typography>
      </Grid>
      {isEditView && !isMobile && (
        <Grid item xs={fieldXs}>
          <Typography variant="body1" fontWeight="normal" sx={{ alignSelf: "center" }}>
            {initialText(initialState?.CostOfOptionsWithContract)}{" "}
            {changeCountText(changeCount?.CostOfOptionsWithContract ?? 0)}
          </Typography>
        </Grid>
      )}
      <Grid item xs={fieldXs}>
        {isView ? (
          <TextField
            variant="outlined"
            size="small"
            fullWidth
            disabled
            value={state.CostOfOptionsWithContract ?? ""}
            InputProps={{ startAdornment: dollarAdornment }}
            sx={viewFieldSx}
          />
        ) : isEditView ? (
          <TextField
            disabled={mode === "review"}
            type="text"
            inputProps={{ inputMode: "numeric", min: 0 }}
            variant="outlined"
            size="small"
            value={
              state.CostOfOptionsWithContract === undefined
                ? ""
                : state.CostOfOptionsWithContract.toString().replace(/^0+/, "")
            }
            onChange={(e) =>
              updateField("CostOfOptionsWithContract", e.target.value)
            }
            error={formErrors["CostOfOptionsWithContract"] !== ""}
            helperText={formErrors["CostOfOptionsWithContract"]}
            fullWidth
            InputProps={{ startAdornment: dollarAdornment }}
          />
        ) : (
          <MoneyField
            label="Cost of Options with Contract"
            value={state.CostOfOptionsWithContract ?? null}
            onChange={(v) =>
              updateField("CostOfOptionsWithContract", v === null ? "" : String(v))
            }
            disabled={mode === "review"}
            allowNegative={false}
            min={0}
            max={9999999.9999}
            error={formErrors["CostOfOptionsWithContract"] !== ""}
            helperText={formErrors["CostOfOptionsWithContract"]}
          />
        )}
      </Grid>

      {/* Cost of Total Options */}
      <Grid item xs={isMobile ? 12 : 9} display="flex" justifyContent={isMobile ? "flex-start" : "flex-end"}>
        <Typography variant="body2" color="text.secondary" sx={{ alignSelf: "center" }}>
          Cost of Total Options
        </Typography>
      </Grid>
      <Grid item xs={fieldXs}>
        <TextField
          variant="filled"
          size="small"
          type="number"
          inputProps={{ min: 0 }}
          disabled
          value={total}
          fullWidth
          InputProps={{ startAdornment: dollarAdornment }}
        />
      </Grid>

      {/* Total Labor & Materials */}
      <Grid item xs={isMobile ? 12 : 9} display="flex" justifyContent={isMobile ? "flex-start" : "flex-end"}
        mt={5}
      >
        <Typography variant="body2" color="text.secondary" sx={{ alignSelf: "center" }}>
          Total Labor & Materials
        </Typography>
      </Grid>
      <Grid item xs={fieldXs} mt={isMobile ? 0 : 5}>
        <TextField
          variant="filled"
          size="small"
          type="number"
          inputProps={{ min: 0 }}
          disabled
          value={totalLaborMaterial}
          fullWidth
          InputProps={{ startAdornment: dollarAdornment }}
        />
      </Grid>
    </SectionAccordion>
  );
}
